import type { Conversation, ConversationStateDef, FolderId, TagDef } from '@/types';
import type { LabelKey } from '@/i18n/labels';
import type { Lang } from '@/stores/langStore';
import type { IconName } from '@/components/ui/Icon';
import { permissionsForRoles, type Permission } from '@/auth/permissions';
import { isTenantAdmin } from '@/auth/roles';
import { controllerId, lockedByOtherOwner } from '@/utils/ownership';

/**
 * The console folder rules (ADR-033, CONTRACT-C), ported from the approved prototype's state/folders.ts onto the
 * server-provided row fields (`ownerAgentId`, `ownerReleased`, `lastCustomerMessageAt`, `lastOutboundAt`, `tags`).
 *
 * <b>The server list is the authority.</b> `GET /api/sessions?folder=` and `GET /api/sessions/counts` build every folder
 * from ONE server-side filter (SessionFolderFilters); the predicates below are a CLIENT COPY used for exactly one
 * thing: re-bucketing a row a push just changed between server pages (`selectFolderRows`' live extras), so a claimed
 * conversation appears in "My consultations" (and a takeover moves from "Need Consultation" to "TakeOver") at once
 * instead of at the next reload. They must
 * never decide what a folder LISTS, never feed a count, and a row they cannot judge (owner unknown) matches nothing.
 *
 * <b>The console never decides ownership.</b> It reads the wire owner; this file only compares it with the signed-in
 * agent's id. Everything here is UX: the server re-checks every action, and (from PR-A2) refuses what it must.
 *
 * <b>Fail closed.</b> `ownerAgentId` has three values and they are not the same: a string (owned), `null` (the server
 * said nobody owns it) and `undefined` (the server did not say - a row built from a push). Unknown is never "shared"
 * and never "mine": it belongs to no ownership folder, and under private ownership it is not visible to a viewer who
 * cannot see everyone. A blank `me` owns nothing - a blank id must never match a blank owner.
 *
 * <b>Legacy shared mode.</b> `ownershipEnforced` is the server's statement that private ownership is IN EFFECT. While
 * it is false (every tenant, until PR-A2 is deployed AND armed) the console renders the pre-ADR-033 shared behaviour:
 * every row is visible and nobody is locked out of acting because of who owns it. The flag is read from the WIRE only
 * (the row, or the list envelope the caller passes in) - never from a config key, and absent means false.
 */

/** Who is looking: the signed-in staff id and the raw role claims of their token. Both come from auth, never a push. */
export interface FolderCtx {
  /** The signed-in staff id. Null/blank = unknown, which owns nothing. */
  me: string | null | undefined;
  /** The token's role claims (raw or canonical). Empty = no permissions (fail closed). */
  roles: readonly string[];
  /**
   * The key of the tenant's closed (isSystem) status, when known. `cat:<closedKey>` lists every CLOSED conversation
   * whatever category it carries; any other `cat:<key>` is that category AND open. Unknown = every `cat:` is open-only.
   */
  closedKey?: string | null;
}

export type FolderSection = 'work' | 'team' | 'more';

export interface SystemFolder {
  /** The folder id exactly as the server spells it (case-sensitive), except `drafts`, which is no list folder. */
  id: string;
  section: FolderSection;
  icon: IconName;
  labelKey: LabelKey;
  /** The rule behind the folder, shown as its tooltip. Keep identical to the server's folder definitions (ADR-033). */
  ruleKey: LabelKey;
  /** The folder's own rule, WITHOUT the viewer's visibility (that is ANDed on by {@link folderMatcher}). */
  match: (c: Conversation, ctx: FolderCtx) => boolean;
  /** The permission the viewer must hold for the folder to exist for them at all. */
  requires?: Permission;
}

/** The folder opened when nothing (or something unusable) is asked for. */
export const DEFAULT_FOLDER: FolderId = 'mine';

/** The section header of each system-folder section. */
export const FOLDER_SECTION_LABEL: Readonly<Record<FolderSection, LabelKey>> = {
  work: 'fr_sec_my_work',
  team: 'fr_sec_team_shared',
  more: 'fr_sec_more',
};

// ── Row facts ────────────────────────────────────────────────────────────────────────────────────────────────

const isBlank = (v: string | null | undefined): boolean => typeof v !== 'string' || v.trim().length === 0;

/**
 * The owner as three values: a trimmed staff id, `null` (nobody owns it) or `undefined` (UNKNOWN - the server did not
 * say, or the row was built from a push and has not been re-read). A blank id names nobody.
 */
function ownerOf(c: Conversation): string | null | undefined {
  if (c.partial || c.ownerAgentId === undefined) return undefined;
  if (c.ownerAgentId === null || isBlank(c.ownerAgentId)) return null;
  return c.ownerAgentId.trim();
}

/** Is `owner` (a named id) the signed-in agent? A blank `me` is nobody. */
const isMe = (owner: string | null | undefined, me: string | null | undefined): boolean =>
  typeof owner === 'string' && !isBlank(me) && owner === (me as string).trim();

const isOpen = (c: Conversation): boolean => c.state !== 'CLOSED';

/** Open, owned, and the owner is me. */
const mine = (c: Conversation, ctx: FolderCtx): boolean => isOpen(c) && isMe(ownerOf(c), ctx.me);

/**
 * The server has described this row's owner (a staff id or `null`), so the row can be placed. A row whose owner is
 * UNKNOWN (a push placeholder, or a row that did not say) belongs to no folder here - not even a team folder, whose rule
 * does not test the owner: a placeholder's state is not the server's word either, and the server list places it.
 */
const described = (c: Conversation): boolean => ownerOf(c) !== undefined;

const hasPermission = (roles: readonly string[], p: Permission): boolean => permissionsForRoles(roles ?? []).has(p);

const released = (c: Conversation): boolean => c.ownerReleased === true;

// ── The system folders ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The system folders, in the order the rail shows them. There is deliberately no `following` and no `snoozed` (D9).
 * The legacy team ids `waiting`, `all-shared` and `teammates` are GONE (PR-A, 2026-10-26): the server treats them as an
 * alias of `mine` and no longer counts them, and a URL that still names one opens {@link DEFAULT_FOLDER}. The conversation
 * status overhaul (2026-11) retired `bot-handle`, `need-consult`, `takeover` (the lifecycle tabs replace them) and
 * `all-closed` (the closed status's own folder): see {@link LEGACY_FOLDER_REMAP}.
 * `drafts` is not a list folder (OP-582): its rows come from the pending-drafts feed, so its predicate is only the
 * live re-bucketing copy and `isListFolder('drafts')` is false.
 */
export const SYSTEM_FOLDERS: readonly SystemFolder[] = [
  // My work - owned by me, visible only to me (+ viewers who may read everyone's).
  { id: 'mine', section: 'work', icon: 'user', labelKey: 'f_mine', ruleKey: 'f_mine_rule', match: mine },
  {
    id: 'my-consult',
    section: 'work',
    icon: 'lifebuoy',
    labelKey: 'f_my_consult',
    ruleKey: 'f_my_consult_rule',
    match: (c, ctx) => mine(c, ctx) && c.state !== 'HUMAN_TAKEOVER' && !released(c),
  },
  {
    id: 'my-takeover',
    section: 'work',
    icon: 'hand',
    labelKey: 'f_my_takeover',
    ruleKey: 'f_my_takeover_rule',
    match: (c, ctx) => mine(c, ctx) && c.state === 'HUMAN_TAKEOVER',
  },
  {
    id: 'my-released',
    section: 'work',
    icon: 'undo',
    labelKey: 'f_my_released',
    ruleKey: 'f_my_released_rule',
    match: (c, ctx) => mine(c, ctx) && released(c) && c.state !== 'HUMAN_TAKEOVER',
  },

  // Team - "All open" is the one fixed team folder. The folders after it are the tenant's conversation STATUSES (one
  // `cat:<key>` per active status, the closed system status included - the rail builds them from the state defs).
  // WHO may see which row is the server's (private conversations on: own + unowned, `workspace.viewAll` sees all; off:
  // everyone sees everything) and is ANDed on by folderMatcher's canSee - these rules never test WHO owns a row, only
  // that the server described it (see `described`).
  {
    id: 'all-open',
    section: 'team',
    icon: 'inbox',
    labelKey: 'f_all_open',
    ruleKey: 'f_all_open_rule',
    match: (c) => described(c) && isOpen(c),
  },

  // More
  {
    id: 'drafts',
    section: 'more',
    icon: 'pencil',
    labelKey: 'f_drafts',
    ruleKey: 'f_drafts_rule',
    match: (c) => isOpen(c) && !!c.pendingDraft,
  },
];

const SYSTEM_BY_ID: ReadonlyMap<string, SystemFolder> = new Map(SYSTEM_FOLDERS.map((f) => [f.id, f]));

/** The system folders this viewer has at all (a folder with `requires` only for a viewer holding that permission). */
export function visibleSystemFolders(roles: readonly string[]): SystemFolder[] {
  return SYSTEM_FOLDERS.filter((f) => !f.requires || hasPermission(roles, f.requires));
}

/** `cat:<key>` (an ADR-017 category) and `tag:<key>` (a catalog tag) folder ids. */
export const categoryFolderId = (key: string): FolderId => `cat:${key}`;
export const tagFolderId = (key: string): FolderId => `tag:${key}`;

/**
 * The key of the tenant's closed status: the active def flagged `isSystem` (the server guarantees exactly one), else
 * the one that maps to lifecycle CLOSED. null = the defs are not loaded (or hold none): the caller must not guess a key.
 */
export function closedStatusKey(stateDefs: readonly ConversationStateDef[] | null | undefined): string | null {
  const defs = Array.isArray(stateDefs) ? stateDefs : [];
  const active = defs.filter((d) => d.isActive !== false);
  const sys = active.find((d) => d.isSystem === true) ?? active.find((d) => (d.mapsToLifecycle ?? '').toUpperCase() === 'CLOSED');
  return sys ? sys.key : null;
}

/** Folder ids the overhaul retired, and where a saved or linked one goes now (`all-closed` needs the closed status key). */
export const LEGACY_FOLDER_REMAP: Readonly<Record<string, FolderId | 'closed-status'>> = {
  'all-closed': 'closed-status',
  'bot-handle': 'all-open',
  'need-consult': 'all-open',
  takeover: 'all-open',
};

/**
 * Map a persisted / saved / linked folder id that no longer exists onto the folder that replaces it: `all-closed` becomes
 * `cat:<closed status key>` (`all-open` while the status defs are not known yet), and `bot-handle`, `need-consult` and
 * `takeover` become `all-open`. Any other id is returned unchanged.
 */
export function remapRetiredFolder(id: FolderId, closedKey?: string | null): FolderId {
  const to = Object.prototype.hasOwnProperty.call(LEGACY_FOLDER_REMAP, id) ? LEGACY_FOLDER_REMAP[id] : undefined;
  if (to === undefined) return id;
  if (to === 'closed-status') return closedKey ? categoryFolderId(closedKey) : 'all-open';
  return to;
}

/** Does this folder id come from `GET /api/sessions?folder=`? Everything but `drafts` (the drafts feed) and a search. */
export function isListFolder(id: FolderId): boolean {
  return id !== 'drafts' && !isSearchFolder(id);
}

// ── The search view (PR-C3, C3-1) ────────────────────────────────────────────────────────────────────────────

/** The search view is a folder-shaped id - `search:<q>` - so it pages with the SAME keyset loader as every other folder. */
export const SEARCH_PREFIX = 'search:';
/** Typing fewer characters than this sends NOTHING to the server. */
export const SEARCH_MIN_CHARS = 2;
/** The longest query the console will send (the input's `maxLength`; a longer value in a URL is not a search). */
export const SEARCH_MAX_CHARS = 100;
/** A typed query is sent this long after the LAST keystroke. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Is this the search view's folder id? */
export const isSearchFolder = (id: FolderId): boolean => id.startsWith(SEARCH_PREFIX);

/**
 * The folder id of a search for `raw`, or null when it is not a search the console will send: the TRIMMED text must be
 * {@link SEARCH_MIN_CHARS}..{@link SEARCH_MAX_CHARS} characters with no control characters. The id carries the trimmed
 * text, so two spellings of one query are one view. The text is only ever carried (URL-encoded by the URL and the HTTP
 * client); it is never built into a pattern or a DOM string here.
 */
export function searchFolderId(raw: string | null | undefined): FolderId | null {
  if (typeof raw !== 'string') return null;
  const q = raw.trim();
  if (q.length < SEARCH_MIN_CHARS || q.length > SEARCH_MAX_CHARS) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(q)) return null;
  return SEARCH_PREFIX + q;
}

/** The query of a search folder id (as {@link searchFolderId} spells it), or null when the id is not a usable search. */
export function searchQueryOf(id: FolderId): string | null {
  if (!isSearchFolder(id)) return null;
  const folder = searchFolderId(id.slice(SEARCH_PREFIX.length));
  return folder === null ? null : folder.slice(SEARCH_PREFIX.length);
}

/**
 * The key of a `cat:`/`tag:` folder, or null when the id is not one or the key is unusable. Mirrors the server's
 * `IsUsableKey`: 1-100 characters, no control characters, no leading or trailing whitespace.
 */
function prefixedKey(id: FolderId, prefix: 'cat:' | 'tag:'): string | null {
  if (!id.startsWith(prefix)) return null;
  const key = id.slice(prefix.length);
  if (key.length === 0 || key.length > 100) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(key)) return null;
  if (key.trim().length !== key.length) return null;
  return key;
}

// ── Visibility and actions (UX only) ─────────────────────────────────────────────────────────────────────────

/**
 * Is private ownership in effect for this row? The caller's value wins (the freshest list envelope); otherwise the
 * row's own wire value; absent is false (legacy shared behaviour).
 */
const enforcedFor = (c: Conversation, ownershipEnforced?: boolean): boolean => (ownershipEnforced ?? c.ownershipEnforced) === true;

/**
 * May this viewer SEE the row at all. UX only - under private ownership the server will not send a row the viewer may
 * not see; this only keeps a stale cached row off the screen.
 *
 *   not enforced                       -> yes (legacy shared behaviour)
 *   viewer holds `workspace.viewAll`   -> yes (reads everything, acts on nothing that is not theirs)
 *   nobody owns it (server said null)  -> yes
 *   I own it                           -> yes
 *   someone else owns it / owner UNKNOWN -> no (unknown fails closed)
 */
export function canSee(
  c: Conversation,
  me: string | null | undefined,
  roles: readonly string[],
  ownershipEnforced?: boolean,
): boolean {
  if (!enforcedFor(c, ownershipEnforced)) return true;
  if (hasPermission(roles, 'workspace.viewAll')) return true;
  const owner = ownerOf(c);
  if (owner === undefined) return false;
  if (owner === null) return true;
  return isMe(owner, me);
}

/**
 * Must the answer bar, Take over, Close and the tag editor be OFF for this viewer on this row? True when:
 *  - the viewer is unidentified, or lacks `workspace.handle` (a viewer who may only read);
 *  - the row is HUMAN_TAKEOVER and no HOLDER can be named (C1's rule: an unknown holder during takeover is read-only,
 *    in legacy mode too - a stale row must never become permission to write to a customer). The holder is the OWNER
 *    under private ownership and the live CONTROLLER (`assignedAgentId`) in legacy shared mode (utils/ownership.ts
 *    holderId): a takeover by B of what A guided keeps owner A and is held by B;
 *  - private ownership is in effect and someone else owns it (D1: `workspace.viewAll` reads it, never acts on it), or
 *    the owner is unknown (unknown fails closed).
 * In legacy shared mode nobody is locked out because of WHO owns the row - only a live takeover held by someone else is. A closed conversation is the caller's
 * concern (it renders read-only text); ownership is all this answers.
 */
export function isReadOnlyFor(
  c: Conversation,
  me: string | null | undefined,
  roles: readonly string[],
  ownershipEnforced?: boolean,
): boolean {
  if (isBlank(me)) return true;
  if (!hasPermission(roles, 'workspace.handle')) return true;

  const owner = ownerOf(c);
  const enforced = enforcedFor(c, ownershipEnforced);
  if (!enforced) {
    // Legacy shared mode locks only a live takeover, and what matters there is who HOLDS it (the live controller), not the
    // recorded owner (same rule as the chat header, lockedByOtherOwner): nobody named = unknown = read-only.
    if (c.state !== 'HUMAN_TAKEOVER') return false;
    const holder = controllerId(c);
    return holder === null || lockedByOtherOwner({ ownershipEnforced: false, state: c.state }, holder, me);
  }
  if (c.state === 'HUMAN_TAKEOVER' && typeof owner !== 'string') return true;
  if (owner === undefined) return true;
  if (owner === null) return false;
  return !isMe(owner, me);
}

/**
 * Offer "Return to team" (D2)? The owner may always give a conversation up; on one the viewer does NOT own it is
 * shown ONLY to a tenant admin (`admin` / `platformAdmin`) - never to a senior agent, never to a plain agent. A row
 * nobody owns (or whose owner is unknown) has nothing to return.
 */
export function canReturnToTeam(c: Conversation, me: string | null | undefined, roles: readonly string[]): boolean {
  const owner = ownerOf(c);
  if (typeof owner !== 'string') return false;
  return isMe(owner, me) || isTenantAdmin(roles);
}

/**
 * Offer "Take over"? Not on a closed conversation (no Reopen, D7), and only for a viewer who may act (holds
 * `workspace.handle` - a viewer who holds only `workspace.viewAll` may read, D1). Under private ownership the row must
 * be unowned or mine; an owner that is someone else's, or unknown, blocks it. In legacy shared mode ownership never
 * blocks it. Whether the row is ALREADY in takeover is the caller's concern.
 */
export function canTakeOver(
  c: Conversation,
  me: string | null | undefined,
  roles: readonly string[],
  ownershipEnforced?: boolean,
): boolean {
  if (!isOpen(c)) return false;
  if (isBlank(me)) return false;
  if (!hasPermission(roles, 'workspace.handle')) return false;
  const owner = ownerOf(c);
  if (!enforcedFor(c, ownershipEnforced)) {
    // Legacy shared mode: a recorded owner never blocks it; a live takeover blocks it unless I am the one holding it, and a
    // takeover nobody can be named as holding is unknown (blocks it) - judged on the live controller, not the owner.
    if (c.state !== 'HUMAN_TAKEOVER') return true;
    const holder = controllerId(c);
    return holder !== null && !lockedByOtherOwner({ ownershipEnforced: false, state: c.state }, holder, me);
  }
  if (c.state === 'HUMAN_TAKEOVER' && typeof owner !== 'string') return false;
  return owner === null || isMe(owner, me);
}

// ── Matching ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The client predicate for one folder, ANDed with the viewer's visibility. An unknown folder id, or a folder the
 * viewer lacks the permission for, matches nothing.
 *
 * `drafts` and `search:*` are the two folders whose ROWS the server already filtered for this viewer (the drafts feed
 * and the search answer), so a row whose owner is merely UNKNOWN (a draft row built from the feed has not been
 * re-read) is not hidden by the visibility check there; a row whose owner is KNOWN and not the viewer's still is.
 *
 * Used only to re-bucket pushed rows between server pages (`selectFolderRows`). The server list wins on every load.
 */
export function folderMatcher(id: FolderId, ctx: FolderCtx, ownershipEnforced?: boolean): (c: Conversation) => boolean {
  let base: (c: Conversation) => boolean;
  let trustServerFilter = false;

  const catKey = prefixedKey(id, 'cat:');
  const tagKey = prefixedKey(id, 'tag:');
  if (catKey !== null) {
    // The closed status lists every CLOSED conversation whatever category it carries; any other status is that category AND open.
    base = ctx.closedKey && catKey === ctx.closedKey ? (c) => !isOpen(c) : (c) => c.category === catKey && isOpen(c);
  } else if (tagKey !== null) {
    base = (c) => (c.tags ?? []).includes(tagKey);
  } else if (isSearchFolder(id)) {
    base = () => true;
    trustServerFilter = true;
  } else {
    const folder = SYSTEM_BY_ID.get(id);
    if (!folder || (folder.requires && !hasPermission(ctx.roles, folder.requires))) return () => false;
    base = (c) => folder.match(c, ctx);
    trustServerFilter = id === 'drafts';
  }

  return (c) => {
    if (trustServerFilter && ownerOf(c) === undefined) return base(c);
    return canSee(c, ctx.me, ctx.roles, ownershipEnforced) && base(c);
  };
}

/** Does this row belong in this folder for this viewer? ({@link folderMatcher} for a single row.) */
export function rowBelongs(
  folder: FolderId,
  c: Conversation,
  me: string | null | undefined,
  roles: readonly string[],
  ownershipEnforced?: boolean,
): boolean {
  return folderMatcher(folder, { me, roles }, ownershipEnforced)(c);
}

// ── Labels and URL parsing ───────────────────────────────────────────────────────────────────────────────────

/**
 * The display name of a folder: a system folder's label, a category's per-tenant bilingual label (its key when the
 * definition is gone), or `# <tag label>` (the raw key, muted by the caller, when the catalog does not know it).
 */
export function folderLabel(
  id: FolderId,
  stateDefs: readonly ConversationStateDef[],
  tagCatalog: readonly TagDef[],
  t: (key: LabelKey) => string,
  lang: Lang,
): string {
  if (isSearchFolder(id)) return t('search_results');
  const catKey = prefixedKey(id, 'cat:');
  if (catKey !== null) return stateDefs.find((d) => d.key === catKey)?.label?.[lang] || catKey;
  const tagKey = prefixedKey(id, 'tag:');
  if (tagKey !== null) return `# ${tagCatalog.find((x) => x.key === tagKey)?.label || tagKey}`;
  const system = SYSTEM_BY_ID.get(id);
  return system ? t(system.labelKey) : id;
}

/** The rule text (tooltip) of a system folder; null for `cat:`/`tag:`/unknown ids. */
export function folderRule(id: FolderId, t: (key: LabelKey) => string): string | null {
  const system = SYSTEM_BY_ID.get(id);
  return system ? t(system.ruleKey) : null;
}

/**
 * The folder a URL value asks for, or {@link DEFAULT_FOLDER}. Ids are case-sensitive and compared exactly: an unknown
 * id (including the retired `waiting` / `all-shared` / `teammates`), a folder whose `requires` the viewer lacks, a
 * `cat:`/`tag:` with an unusable key, and a
 * blank value all open the default folder. The four folders the status overhaul retired are REMAPPED first
 * ({@link remapRetiredFolder}). A search IS addressable (`?folder=search:<q>`, so a refresh and the back button
 * land on it): it is accepted only as {@link searchFolderId} spells it (2-100 characters once trimmed) and is returned in
 * that normalised spelling; anything else opens the default folder.
 */
export function parseFolderParam(
  urlValue: string | null | undefined,
  roles: readonly string[],
  closedKey?: string | null,
): FolderId {
  if (typeof urlValue !== 'string') return DEFAULT_FOLDER;
  urlValue = remapRetiredFolder(urlValue, closedKey);
  if (isSearchFolder(urlValue)) return searchFolderId(urlValue.slice(SEARCH_PREFIX.length)) ?? DEFAULT_FOLDER;
  const system = SYSTEM_BY_ID.get(urlValue);
  if (system) return !system.requires || hasPermission(roles, system.requires) ? system.id : DEFAULT_FOLDER;
  if (prefixedKey(urlValue, 'cat:') !== null || prefixedKey(urlValue, 'tag:') !== null) return urlValue;
  return DEFAULT_FOLDER;
}
